
import { Component, Vue } from 'vue-property-decorator';
import '@toast-ui/editor/dist/toastui-editor-viewer.css';
import { getLeagueDetail, getLeagueTeams, applyToLeague } from '@/api/league';
import { getMyTeams, checkMembershipStatus } from '@/api/team';
import { UserModule } from '@/store/modules/user';

@Component({ name: 'LeagueDetail' })
export default class extends Vue {
  private isLoading = false;

  private applying = false;

  private league: any = null;

  private isOwner = false;

  private alreadyJoined = false;

  get statusTagType(): string {
    if (!this.league) return '';
    return ({ RECRUITING: 'success', IN_PROGRESS: 'warning', COMPLETED: 'info' } as any)[this.league.status] || 'info';
  }

  get statusLabel(): string {
    if (!this.league) return '';
    return ({ RECRUITING: '모집중', IN_PROGRESS: '진행중', COMPLETED: '완료' } as any)[this.league.status] || this.league.status;
  }

  get joinButtonDisabled(): boolean {
    if (!UserModule.isLogin) return true;
    if (!this.league) return true;
    if (this.league.status !== 'RECRUITING') return true;
    if (this.league.currentTeams >= this.league.maxTeams) return true;
    if (this.alreadyJoined) return true;
    if (!this.isOwner) return true;
    return false;
  }

  get joinButtonText(): string {
    if (!UserModule.isLogin) return '로그인 후 이용 가능합니다';
    if (!this.league) return '리그 참여하기';
    if (this.league.status !== 'RECRUITING') return '모집중이 아닙니다';
    if (this.league.currentTeams >= this.league.maxTeams) return '모집 마감';
    if (this.alreadyJoined) return '이미 참여 중';
    if (!this.isOwner) return '팀 운영자만 신청 가능';
    return '리그 참여하기';
  }

  get safeIntroContent(): string {
    const raw = this.league?.introContent || '';
    return raw
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, '')
      .replace(/\son\w+\s*=\s*"(?:[^"]*)"/gi, '')
      .replace(/\son\w+\s*=\s*'(?:[^']*)'/gi, '')
      .replace(/javascript:/gi, '');
  }

  async created(): Promise<void> {
    await this.fetchData();
  }

  private async fetchData(): Promise<void> {
    this.isLoading = true;
    try {
      const uid = this.$route.params.uid;
      const [leagueRes, teamsRes] = await Promise.all([
        getLeagueDetail(uid),
        getLeagueTeams(uid),
      ]);
      this.league = leagueRes.data;
      const leagueTeams: any[] = teamsRes.data || [];

      if (UserModule.isLogin) {
        try {
          const statusRes = await checkMembershipStatus();
          this.isOwner = !!statusRes.data?.hasCreatedTeam;
          if (this.isOwner) {
            const myRes = await getMyTeams();
            const myTeam: any = myRes.data;
            if (myTeam && myTeam.uid) {
              this.alreadyJoined = leagueTeams.some((t: any) => t.teamUid === myTeam.uid);
            }
          }
        } catch {
          // 팀 정보 조회 실패는 무시
        }
      }
    } catch {
      this.league = null;
    } finally {
      this.isLoading = false;
    }
  }

  private async handleJoin(): Promise<void> {
    if (!this.league) return;
    if (this.league.participationPoints > 0) {
      try {
        await this.$confirm(
          `참여비 ${this.league.participationPoints.toLocaleString()} 캐쉬가 차감됩니다.\n리그에 참여하시겠습니까?`,
          '리그 참여 확인',
          { confirmButtonText: '참여', cancelButtonText: '취소', type: 'warning' },
        );
      } catch {
        return;
      }
    }
    this.applying = true;
    try {
      await applyToLeague(this.$route.params.uid);
      this.$message.success('리그에 참여했습니다!');
      this.alreadyJoined = true;
      if (this.league) this.league.currentTeams += 1;
    } catch (e: any) {
      const msg = e?.response?.data?.message || '참여 중 오류가 발생했습니다.';
      this.$message.error(msg);
    } finally {
      this.applying = false;
    }
  }
}
